import { useState, useEffect, useRef } from 'react';
import { panelManager } from './panelManager';

interface IUseTriggerToggleProps {
  panelId: string,
  positioning?: 'relative' | 'fixed',
  offsetSelector?: string,
}

interface IUseTriggerToggleReturn {
  isOpen: boolean,
  togglePanel: () => void,
  panelRef: React.RefObject<HTMLDivElement>,
}

const useTriggerToggle = ({
  panelId,
  positioning = 'relative',
  offsetSelector,
}: IUseTriggerToggleProps): IUseTriggerToggleReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = panelManager.subscribe((state) => {
      setIsOpen(state.openId === panelId);
    });

    setIsOpen(panelManager.getState().openId === panelId);

    return unsubscribe;
  }, [panelId]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isOpen && panelRef.current && positioning === 'fixed' && offsetSelector) {
      const updateOffset = () => {
        const offsetElement = document.querySelector(offsetSelector) as HTMLElement;
        if (offsetElement && panelRef.current) {
          const rect = offsetElement.getBoundingClientRect();
          panelRef.current.style.setProperty('--panel-offset-top', `${ rect.bottom }px`);
        }
      };

      updateOffset();
      window.addEventListener('resize', updateOffset);
      return () => window.removeEventListener('resize', updateOffset);
    }
  }, [isOpen, positioning, offsetSelector]);

  const togglePanel = () => {
    panelManager.toggle(panelId);
  };

  return { isOpen, togglePanel, panelRef };
};

export default useTriggerToggle;
